import Loader from 'components/Loader';
import { FC, useCallback } from 'react';
import { useDeleteHistoryMutation, useGetHistoryQuery, useUpdateHistoryMutation } from 'store/api/histories';
import { HistoryEntityDto, HistoryFieldType, HistoryForm, UpdateHistoryDto } from 'types/histories';
import HistoryModalContent from './HistoryModalContent';

// Field types that opt in to deleting individual history records. Receivability is the only one for
// now: its current/open record can be deleted because the backend reverts the invoice's live value.
const DELETE_ENABLED_FIELD_TYPES: ReadonlySet<string> = new Set([HistoryFieldType.RECEIVABILITY_TYPE_CODE]);

/** The history as the new design's side sheet, beside the record: what it names under its title, and what it is. */
export interface HistorySheet {
    subtitle?: string;
    description?: string;
}

export interface HistoryModalProps {
    titleId?: string;
    entityType: string;
    entityId: string;
    fieldType: string;
    fieldName: string;
    activity: string;
    onClose: () => void;
    /** Drawn as a side sheet beside the record instead of a dialog over it. */
    detached?: HistorySheet;
}

export type HistoryFormData = {
    histories: HistoryForm[];
};

const HistoryModal: FC<HistoryModalProps> = (props) => {
    const {
        titleId = 'history.modal.title',
        entityType,
        entityId,
        fieldType,
        fieldName,
        activity,
        onClose,
        detached,
    } = props;

    const [updateHistory] = useUpdateHistoryMutation();
    const [deleteHistory] = useDeleteHistoryMutation();
    const { data: historyData, isLoading: isHistoryDataLoading } = useGetHistoryQuery({
        entityType,
        entityId,
        fieldType,
    });

    const handleUpdate = useCallback(
        async (props: HistoryEntityDto & UpdateHistoryDto & { entityType?: string; fieldType?: string }) => {
            const { endDate, entityId, entityType, fieldType, id, startDate } = props as HistoryEntityDto &
                UpdateHistoryDto & { entityType: string; fieldType: string };
            await updateHistory({ body: { startDate, endDate }, entityId, entityType, fieldType, historyId: id });
        },
        [updateHistory]
    );

    const handleDelete = useCallback(
        async ({ id }: HistoryEntityDto) => {
            await deleteHistory({ historyId: id, entityType, entityId, fieldType });
        },
        [deleteHistory, entityType, entityId, fieldType]
    );

    if (isHistoryDataLoading) {
        return <Loader />;
    }

    return (
        <HistoryModalContent
            activity={activity}
            fieldName={fieldName}
            fieldType={fieldType}
            onClose={onClose}
            titleId={titleId}
            historyData={historyData}
            handleUpdate={handleUpdate}
            handleDelete={DELETE_ENABLED_FIELD_TYPES.has(fieldType) ? handleDelete : undefined}
            detached={detached}
        />
    );
};

export default HistoryModal;
